import { globalConstants, namespace } from 'wsm-tagging-utils';

const {
	SPECIALS_EVENT,
	THIRD_PARTY_USER_EVENT,
	TRACK_EVENT,
	WIDGET_USER_EVENT
} = globalConstants;

const runInteractionListener = (win) => {
	namespace(win, 'DDC.tracking.framework.runtime');

	const {
		DDC: { tracking },
		com
	} = win;

	tracking.framework.runtime.InteractionListener =
		(function buildInteractionListener() {
			// ======================================================================
			// --------------- Private Static Variables and Functions ---------------
			// ======================================================================

			/**
			 * Maintains a one-to-one mapping of event type to event handler method
			 * to call when that event occurs.
			 */
			const handlerMapping = {
				'ddc.page.view': 'handlePageView',
				'ddc.page.vehicleDetailView': 'handleVehicleDetailView',
				'ddc.inventory.filterSearch': 'handleFilterSearch',
				'ddc.inventory.filterSearch.click': 'handleFilterSearchClick',
				'ddc.inventory.typedSearch': 'handleTypedSearchResults',
				'ddc.inventory.typedSearch.init': 'handleTypedSearchInit',
				'ddc.incentive.filterSearch': 'handleIncentiveFilter',
				'ddc.slideshow.click': 'handleSlideshowClick',
				'ddc.slideshow.pagerClick': 'handleSlideshowPagerClick',
				'ddc.dialog.ajaxContentLoaded': 'handleAjaxContentLoad',
				'ddc.dialog.opened': 'handleDialogOpened',
				'ddc.dialog.closed': 'handleDialogClosed',
				'ddc.form.facetBrowseChange': 'handleFormChange',
				'ddc.form.inventorySearchLeadChange': 'handleFormChange',
				'ddc.form.typedSearch': 'handleTypedSearch',
				'ddc.form.paymentCalcInit': 'handlePaymentCalcInit',
				'ddc.form.confirmation': 'handleFormConfirmation',
				'ddc.form.initiation': 'handleFormInitiation',
				'ddc.form.onPage': 'handleFormOnPage',
				'ddc.form.submission': 'handleFormSubmission',
				'ddc.form.change': 'handleFormChange',
				'ddc.form.slideChange': 'handleFormChange',
				'ddc.link.generic': 'handleLinkClick',
				'ddc.link.clickToCall': 'handleClickToCall',
				'ddc.link.comparePageClick': 'handleComparePageClick',
				'ddc.link.hproductClick': 'handleHproductClick',
				'ddc.link.mycarsLogin': 'handleMycarsLogin',
				'ddc.link.mycarsSave': 'handleMycarsSave',
				'ddc.link.mycarsGetAlerts': 'handleMycarsGetAlerts',
				'ddc.link.mycarsCompareCheck': 'handleMycarsCompareCheck',
				'ddc.link.mycarsCompareSubmit': 'handleMycarsCompareSubmit',
				'ddc.link.offerClick': 'handleOfferClick',
				'ddc.link.navClick': 'handleNavigationClick',
				'ddc.link.recommendedVehicle': 'handleRecommendedVehicleClick',
				'ddc.link.socialHeaderClick': 'handleSocialHeaderClick',
				'ddc.link.socialShare': 'handleSocialShare',
				'ddc.link.getDirections': 'handleGetDirections',
				'ddc.link.vehicleCompareCheck': 'handleVehicleCompareCheck',
				'ddc.maps': 'handleMapClick',
				'ddc.video.play': 'handleVideoPlay',
				'ddc.video.milestone': 'handleVideoMilestone'
				// moved these to trackEvent so the additionalProperties are supported
				// specialsEvent: 'handleSpecialsEvent',
				// widgetUserEvent: 'handleAllWidgetUserEvents',
				// thirdPartyUserEvent: 'handleThirdPartyUserEvent'
			};

			const trackEventHandlerMapping = {
				[WIDGET_USER_EVENT]: 'handleAllWidgetUserEvents',
				[SPECIALS_EVENT]: 'handleSpecialsEvent',
				[THIRD_PARTY_USER_EVENT]: 'handleThirdPartyUserEvent'
			};

			class InteractionListener {
				/**
				 * Creates the InteractionListener object
				 *
				 * @param {Object} window A reference to the global window object
				 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
				 * * @param {array} eventHandlers A list of tracking.framework.runtime.EventHandler objects used to handle events
				 * @param {tracking.framework.runtime.Form} form A Form instance for form related queries
				 * @param {Object} flags An object of feature flags and values
				 * @param {Boolean} pageViewEventHasFired Set to true to skip the logic ensuring page view fires first
				 * @constructor
				 *
				 * @see https://sourcemaking.com/design_patterns/observer
				 * @see https://sourcemaking.com/design_patterns/strategy
				 */
				constructor(
					thisWin,
					page,
					eventHandlers,
					form,
					flags,
					pageViewEventHasFired = false,
					waitOnEventToStartHandling = ''
				) {
					this.log = new com.dealer.log.Logger(
						'ws-tagging:InteractionListener'
					);
					this.win = thisWin;
					this.page = page;
					this.form = form;
					this.eventHandlers = Array.isArray(eventHandlers)
						? eventHandlers
						: [eventHandlers];
					this.flags = flags || {};
					this.pageViewEventHasFired = pageViewEventHasFired;
					this.runningUnitTest = pageViewEventHasFired;
					this.waitOnEventToStartHandling =
						waitOnEventToStartHandling;
					this.hasWaitedToStartHandling = false;
				}

				/**
				 * Adds new event handler chains to the list of handlers this listener is manages
				 *
				 * @param {tracking.framework.runtime.EventHandler} eventHandlers the event handlers to add
				 */
				addEventHandlers(...eventHandlers) {
					eventHandlers.forEach((eventHandler) => {
						if (
							eventHandler instanceof
							tracking.framework.runtime.EventHandler
						) {
							this.log.debug(
								`adding new event handler ${eventHandler.logCategory}`
							);
							this.eventHandlers.push(eventHandler);
						} else {
							this.log.warn(
								`could not add event handler "${eventHandler}"`
							);
						}
					});
				}

				/**
				 * Verifies event pushed to dataLayer follows new "trackEvent" format
				 *
				 * @param {Object} event The event data object
				 * @returns {boolean} true if event is in the new trackEvent format
				 */
				isNewTrackEventFormat = (event) => {
					return !!(
						event.widgetName &&
						event.element &&
						event.action &&
						event.result &&
						event.event === WIDGET_USER_EVENT
					);
				};

				// ======================================================================
				// -------------------------- DataLayer Monitoring ----------------------
				// ======================================================================

				/**
				 * Instructs the InteractionListener that data has been pushed into the DataLayer
				 *
				 * @param {Array} dataLayer The dataLayer being observed
				 * @param {Object} message The data applied to the dataLayer
				 */
				onDataPushed(dataLayer, message) {
					const eventData =
						typeof message.eventData === 'object'
							? message.eventData
							: message;
					const mappedHandlerMethod = handlerMapping[message.event];

					// notify all event handlers in chain
					if (typeof mappedHandlerMethod === 'string') {
						this.log.info(`handling ${message.event} event`);
						this.notifyHandlersAndWaitForPageView(
							mappedHandlerMethod,
							eventData
						);
					}
				}

				checkSatelliteAndNotifyHandler(
					handler,
					handlerMethod,
					eventData
				) {
					if (
						!this.runningUnitTest &&
						handlerMethod === 'handleVideoPlay' &&
						typeof handler?.win?._satellite?.track === 'undefined'
					) {
						// safety check
						const timeout = setTimeout(() => {
							this.skipSatelliteCheck = true;
						}, 5000);

						// keep checking for satelite function to be present
						// or timeout above occurs
						const interval = setInterval(() => {
							if (
								this.skipSatelliteCheck ||
								!(
									typeof handler?.win?._satellite?.track ===
									'undefined'
								)
							) {
								clearInterval(interval);
								clearTimeout(timeout);
								handler[handlerMethod](eventData);
							}
						}, 50);
					} else {
						handler[handlerMethod](eventData);
					}
				}

				/**
				 * Notifies each managed event handler chain that an event has occurred
				 *
				 * @param {string} handlerMethod event handler method to call
				 * @param {Object} eventData event data to pass to the handler
				 */
				notifyHandlers(handlerMethod, eventData) {
					// call each event handler attached to listener, catch exceptions so calls to
					// each handler is not interrupted
					this.eventHandlers.forEach((handler) => {
						try {
							this.log.debug(
								`initiating call to ${handler.logCategory}.${handlerMethod}`
							);

							if (
								this.hasWaitedToStartHandling ||
								!this.waitOnEventToStartHandling
							) {
								this.checkSatelliteAndNotifyHandler(
									handler,
									handlerMethod,
									eventData
								);
							} else {
								this.win.addEventListener(
									this.waitOnEventToStartHandling,
									() => {
										this.checkSatelliteAndNotifyHandler(
											handler,
											handlerMethod,
											eventData
										);
										this.hasWaitedToStartHandling = true;
									}
								);
							}
						} catch (e) {
							this.log.error(
								`error when calling ${handler.logCategory}.${handlerMethod}`
							);
							// print standard stack trace to console
							// eslint-disable-next-line no-console
							console.error(e);
						}
					});
				}

				/**
				 * Calls notifyHandlers and ensure ddc.page.view is called first
				 * The reason to make sure ddc.page.view is called first is because
				 * some clients rely on data that is passed with the page view event
				 * as the basis for the rest of the events on the page. Shift for example.
				 *
				 * @param {string} handlerMethod event handler method to call
				 * @param {Object} eventData event data to pass to the handler
				 */
				notifyHandlersAndWaitForPageView(
					handlerMethod,
					eventData = {}
				) {
					if (handlerMethod === 'handlePageView') {
						this.pageViewEventHasFired = true;
					}

					if (
						!this.pageViewEventHasFired &&
						handlerMethod !== 'handlePageView'
					) {
						// safety check - timeout if handlePageView never happens
						const timeout = setTimeout(() => {
							this.pageViewEventHasFired = true;
						}, 250);

						// keep checking for the page view event to have fired until it does
						// or timeout above occurs
						const interval = setInterval(() => {
							if (this.pageViewEventHasFired) {
								clearInterval(interval);
								clearTimeout(timeout);
								this.notifyHandlers(handlerMethod, eventData);
							}
						}, 50);
					} else {
						this.notifyHandlers(handlerMethod, eventData);
					}
				}

				/**
				 * Instructs the InteractionListener that widgetUserEvents have been observed.
				 *
				 * Previously we were listening for widgetUserEvent's from the onDataPushed function but doing that it is not
				 * possible to pass extra data w/o that data also flowing into GA. Listening to trackEvent allows us to capture
				 * extra data passed with the jquery trigger.
				 *
				 * Result of method is handleWidgetUserEvent notified with data in format:
				 * {'event':'widgetUserEvent',...,optionalData:{...}}
				 *
				 * @returns {undefined}
				 */
				observeTrackEvents() {
					$(document).on(
						TRACK_EVENT,
						(_event, eventData = {}, optionalData = false) => {
							const { event = '', eventName = '' } = eventData;

							if (
								Object.keys(trackEventHandlerMapping).includes(
									event
								)
							) {
								const handlersToNotify = [];

								this.log.info(
									`handling Osiris ${event}${
										eventName && ` - ${eventName}`
									} event`
								);

								const data = {
									...(optionalData && { optionalData }),
									...eventData
								};

								// handle widgetUserEvents in event format used in Osiris widgets
								if (this.isNewTrackEventFormat(eventData)) {
									handlersToNotify.push(
										'handleWidgetUserEvent'
									);
								}

								handlersToNotify.push(
									trackEventHandlerMapping[event]
								);
								handlersToNotify.forEach((handlerToNotify) =>
									this.notifyHandlersAndWaitForPageView(
										handlerToNotify,
										data
									)
								);
							}
						}
					);
				}

				/**
				 * Utilizes a DataLayerHelper to listen for changes to the data layer and invoking
				 * {@link #onDataPushed} when a change is detected.
				 */
				manuallyInitialize() {
					this.log.info('listening to DataLayer events');
					// eslint-disable-next-line no-new
					new win.DataLayerHelper(
						this.win.dataLayer,
						(dataLayer, message) => {
							this.onDataPushed(dataLayer, message);
						},
						true
					);

					this.log.info('listening to Widget User Events');
					this.observeTrackEvents();
				}

				/**
				 * this function is automatically called by Component.js when a new InteractionListener is created
				 * but we do not want to initialize yet
				 * we will manually initialize (using manuallyInitialize function above) once all event handlers have loaded
				 */
				initialize() {
					this.log.info('initialized');
				}
			}

			return InteractionListener;
		})();
};

export default runInteractionListener;
